import type { ReactNode } from 'react';
import { Box, Heading, Paragraph, Tag } from '@zillow/constellation-10';
import type { Document, RequiredDocumentSection } from '../../../../../models/rental-application';
import { usePendingRemovalActions } from '../../../../../hooks/usePendingDocumentRemovals';
import { DocumentCard } from './DocumentCard';
import { DocumentCardMenu } from './DocumentCardMenu';

export type DocumentRemoval = {
  label: string;
  section: RequiredDocumentSection;
};

type CardActions = {
  removal?: DocumentRemoval;
  canDelete: boolean;
};

type Props = CardActions & {
  heading: string;
  tag?: string;
  requirement?: string;
  isRequested?: boolean;
  documents: Array<Document>;
  action?: ReactNode;
};

function StepDocumentCard({ document, removal, canDelete }: CardActions & { document: Document }) {
  const { detach, hideDeleted, isDetaching } = usePendingRemovalActions();
  const { id: documentId } = document;
  const { documentType, incomeId } = removal?.section ?? {};

  const onRemove =
    documentId && documentType
      ? () => void detach({ documentId, documentType, incomeId: incomeId ?? undefined })
      : undefined;

  return (
    <DocumentCard
      document={document}
      onDeleted={hideDeleted}
      deleteConfirmationText="This also deletes it from all applications you have not submitted."
      menu={({ onDelete }) => (
        <DocumentCardMenu
          fileName={document.fileName}
          removeLabel={removal?.label}
          onRemove={onRemove}
          onDelete={canDelete ? onDelete : undefined}
          disabled={!documentId || isDetaching(documentId)}
        />
      )}
    />
  );
}

export function DocumentContainer({
  heading,
  tag,
  requirement,
  isRequested,
  documents,
  removal,
  canDelete,
  action,
}: Props) {
  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
      <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'default' }}>
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tighter', minWidth: 0 }}>
          <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter', flexWrap: 'wrap' }}>
            <Heading level={4} textStyle="body-lg-bold">
              {heading}
            </Heading>
            {tag && (
              <Tag size="sm" css={{ maxWidth: '100%', overflowWrap: 'anywhere' }}>
                {tag}
              </Tag>
            )}
          </Box>
          {requirement && (
            <Paragraph textStyle="body-sm-bold" color="text.subtle">
              {requirement}
            </Paragraph>
          )}
          {isRequested && (
            <Paragraph textStyle="body-sm" color="text.subtle">
              Date coverage is not verified
            </Paragraph>
          )}
        </Box>
        {action}
      </Box>
      {documents.length > 0 ? (
        <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 'tighter' }}>
          {documents.map((document, index) => (
            <Box
              key={document.id ?? `pending-${index}`}
              css={{ gridColumnEnd: { base: 'span 12', md: 'span 6', lg: 'span 4' } }}
            >
              <StepDocumentCard document={document} removal={removal} canDelete={canDelete} />
            </Box>
          ))}
        </Box>
      ) : (
        <Paragraph textStyle="body-sm" color="text.subtle">
          No files added
        </Paragraph>
      )}
    </Box>
  );
}
